import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Space,
  Tag,
  Row,
  Col,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  message,
  Typography,
  Popconfirm,
  Empty,
  Alert,
  Badge
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  GiftOutlined,
  StarOutlined,
  FireOutlined
} from '@ant-design/icons';
import styled from 'styled-components';
import { gamificationApi } from '../../../shared/api/gamificationApi';
import { Rule, Condition, Level } from './types';
import { useTheme } from '../../../contexts/ThemeContext';
import { ExportImportButtons } from './ExportImportButtons';

const { Text, Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const StyledCard = styled(Card)`
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

export const RulesTab: React.FC = () => {
  const [rules, setRules] = useState<Rule[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRule, setEditingRule] = useState<Rule | null>(null);
  const [form] = Form.useForm();
  const [levels, setLevels] = useState<Level[]>([]);

  useEffect(() => {
    loadRules();
    loadLevels();
  }, []);

  const loadRules = async () => {
    try {
      setLoading(true);
      const data = await gamificationApi.getRules();
      setRules(data);
    } catch (error) {
      message.error('Error al cargar reglas');
    } finally {
      setLoading(false);
    }
  };

  const loadLevels = async () => {
    try {
      const data = await gamificationApi.getLevels();
      setLevels(data);
    } catch (error) {
      console.error('Error al cargar niveles:', error);
    }
  };

  const handleCreate = () => {
    setEditingRule(null);
    form.resetFields();
    form.setFieldsValue({
      isActive: true,
      priority: 0,
      bonusConfigs: [{ bonusType: 'PERCENTAGE', stackBehavior: 'STACKABLE', recipientType: 'REFERRER' }],
      conditions: [], // Vacío = aplica siempre
    });
    setModalVisible(true);
  };

  const handleEdit = async (record: Rule) => {
    setEditingRule(record);
    try {
      const fullRule = await gamificationApi.getRule(record.id);


      // ✅ FIX: Transformar condiciones del backend al formato del frontend
      // El backend convierte LOAD_COUNT_MOD → LOAD_COUNT (con modulo)
      // Aquí hacemos el mapeo inverso para mostrar correctamente en el form
      const transformedConditions = (fullRule.conditions || []).map((cond: any) => {
        // Si es LOAD_COUNT con modulo → convertir a LOAD_COUNT_MOD para el UI
        if (cond.conditionType === 'LOAD_COUNT' && cond.modulo !== null && cond.modulo !== undefined) {
          return { ...cond, conditionType: 'LOAD_COUNT_MOD' };
        }
        return cond;
      });

      // Si no tiene bonusConfigs (regla vieja), crear uno desde los campos legacy
      let bonusConfigs = fullRule.bonusConfigs || [];
      if (bonusConfigs.length === 0 && fullRule.bonusType) {
        bonusConfigs = [{
          bonusType: fullRule.bonusType,
          bonusValue: fullRule.bonusValue,
          stackBehavior: fullRule.stackBehavior || 'STACKABLE',
          stackGroup: fullRule.stackGroup
        }];
      }

      const formValues = {
        ...fullRule,
        resetStreakAfter: fullRule.resetStreakAfter ?? null, // Convertir undefined a null
        validFrom: fullRule.validFrom ? fullRule.validFrom : undefined,
        validUntil: fullRule.validUntil ? fullRule.validUntil : undefined,
        conditions: transformedConditions, // Vacío = aplica siempre
        bonusConfigs: bonusConfigs.length > 0 ? bonusConfigs : [{ bonusType: 'PERCENTAGE', stackBehavior: 'STACKABLE', recipientType: 'REFERRER' }],
      };


      // Abrir el modal PRIMERO para que los Form.List se monten
      setModalVisible(true);

      // Luego setear valores con un pequeño delay para asegurar que el form esté montado
      setTimeout(() => {
        form.setFieldsValue(formValues);

      }, 100);
    } catch (error) {
      message.error('Error al cargar la regla');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await gamificationApi.deleteRule(id);
      message.success('Regla eliminada exitosamente');
      loadRules();
    } catch (error) {
      message.error('Error al eliminar regla');
    }
  };

  const handleSubmit = async (values: any) => {
    try {


      // FASE 6: Validación global pre-submit
      if (!values.bonusConfigs || values.bonusConfigs.length === 0) {
        message.error('Debe tener al menos una configuración de bonus');
        return;
      }
      // Condiciones vacías = aplica siempre (no validamos que haya al menos una)

      // VALIDACIÓN REMARKETING: Validar condiciones y bonusConfigs
      const remarketingConditions = values.conditions?.filter((c: any) => c?.conditionType === 'REMARKETING') || [];
      if (remarketingConditions.length > 0) {
        // Verificar que todas las condiciones REMARKETING tengan flagName
        const conditionsWithoutFlagName = remarketingConditions.filter((c: any) => !c.flagName || c.flagName.trim() === '');
        if (conditionsWithoutFlagName.length > 0) {
          message.error('Todas las condiciones de REMARKETING deben tener un nombre de flag definido');
          return;
        }

        // Verificar que todos los bonusConfigs tengan valores
        const invalidBonusConfigs = values.bonusConfigs?.filter((b: any) => !b.bonusValue || b.bonusValue <= 0);
        if (invalidBonusConfigs.length > 0) {
          message.error('Todos los bonus deben tener un valor mayor a 0');
          return;
        }

        // ADVERTENCIA (opcional): Verificar si hay bonusConfigs con remarketingFlagName que no coincidan con ninguna condición
        const conditionFlags = remarketingConditions.map((c: any) => c.flagName).filter(Boolean);
        const bonusConfigsWithFlag = values.bonusConfigs?.filter((b: any) => b.remarketingFlagName && b.remarketingFlagName.trim() !== '') || [];
        const unmatchedBonuses = bonusConfigsWithFlag.filter((b: any) => !conditionFlags.includes(b.remarketingFlagName));

        if (unmatchedBonuses.length > 0) {
          const unmatchedFlags = unmatchedBonuses.map((b: any) => b.remarketingFlagName).join(', ');
          message.warning(`Algunos bonus tienen flags que no están en las condiciones: ${unmatchedFlags}. Estos bonus no se aplicarán.`);
        }
      }

      // FASE 6: Sanitizar valores numéricos (convertir 0/vacío a null)
      const sanitizeNumber = (val: any) => (val === 0 || val === '' || val === undefined || val === null) ? null : val;

      // Preparar payload
      const payload = {
        ...values,
        // Sanitizar campos de nivel
        levelId: values.levelId || null,
        minLevel: values.levelId ? null : sanitizeNumber(values.minLevel), // Si hay levelId, limpiar min/max
        maxLevel: values.levelId ? null : sanitizeNumber(values.maxLevel),
        // Sanitizar restricciones de uso
        maxUsesPerUser: sanitizeNumber(values.maxUsesPerUser),
        maxUsesPerDay: sanitizeNumber(values.maxUsesPerDay),
        maxUsesGlobal: sanitizeNumber(values.maxUsesGlobal),
        rolloverMultiplier: sanitizeNumber(values.rolloverMultiplier),
        // Sanitizar condiciones (excluir campos de UI y convertir tipos virtuales)
        conditions: values.conditions?.map((cond: any) => {
          const { streakType, ...condWithoutStreakType } = cond;

          // Convertir tipos virtuales de UI a tipos reales de Prisma
          let conditionType = condWithoutStreakType.conditionType;
          if (conditionType === 'LOAD_COUNT_MOD') {
            conditionType = 'LOAD_COUNT'; // LOAD_COUNT_MOD es LOAD_COUNT con modulo
          }

          return {
            ...condWithoutStreakType,
            conditionType, // Usar el tipo convertido
            minValue: sanitizeNumber(cond.minValue),
            maxValue: sanitizeNumber(cond.maxValue),
            exactValue: sanitizeNumber(cond.exactValue),
            modulo: sanitizeNumber(cond.modulo),
            moduloEquals: sanitizeNumber(cond.moduloEquals),
          };
        }),
        // Sanitizar bonus configs
        bonusConfigs: values.bonusConfigs?.map((bonus: any) => ({
          ...bonus,
          stackGroup: bonus.stackGroup?.trim() || null,
        })),
      };

      // Solo incluir resetStreakAfter si hay condiciones LOAD_STREAK
      const hasStreakCondition = values.conditions?.some(
        (cond: any) => cond.conditionType === 'LOAD_STREAK'
      );

      if (hasStreakCondition) {
        // Si hay condición LOAD_STREAK, incluir resetStreakAfter (puede ser null o un número)
        payload.resetStreakAfter = values.resetStreakAfter ?? null;
      } else {
        // Si NO hay condición LOAD_STREAK, eliminar resetStreakAfter del payload
        delete payload.resetStreakAfter;
      }


      if (editingRule) {
        await gamificationApi.updateRule(editingRule.id, payload);
        message.success('Regla actualizada exitosamente');
      } else {
        await gamificationApi.createRule(payload);
        message.success('Regla creada exitosamente');
      }
      setModalVisible(false);
      loadRules();
    } catch (error: any) {
      console.error('[DEBUG] Error al guardar:', error);
      message.error(error.response?.data?.error || 'Error al guardar regla');
    }
  };

  const { theme } = useTheme();

  const getBonusTypeConfig = (type: string) => {
    const types: Record<string, { color: string; text: string; bg: string; bgDark: string }> = {
      PERCENTAGE: {
        color: '#1890ff',
        text: 'Porcentaje',
        bg: '#e6f7ff',
        bgDark: '#111d2c'
      },
      FIXED_AMOUNT: {
        color: '#52c41a',
        text: 'Monto Fijo',
        bg: '#f6ffed',
        bgDark: '#162312'
      },
      POINTS: {
        color: '#722ed1',
        text: 'Puntos',
        bg: '#f9f0ff',
        bgDark: '#1d1028'
      },
    };
    return types[type] || { color: '#8c8c8c', text: type, bg: '#fafafa', bgDark: '#1f1f1f' };
  };

  const getConditionDetailText = (condition: Condition): string => {
    const { conditionType, minValue, maxValue, exactValue, modulo, moduloEquals } = condition;

    switch (conditionType) {
      case 'LOAD_COUNT':
        if (minValue && maxValue) return `Entre ${minValue} y ${maxValue} cargas totales`;
        if (minValue) return `Mínimo ${minValue} cargas totales`;
        if (maxValue) return `Máximo ${maxValue} cargas totales`;
        return 'Cantidad de cargas';

      case 'LOAD_COUNT_TODAY':
        if (minValue && maxValue) return `Entre ${minValue} y ${maxValue} cargas hoy`;
        if (minValue) return `Mínimo ${minValue} cargas hoy`;
        if (maxValue) return `Máximo ${maxValue} cargas hoy`;
        return 'Cargas del día';

      case 'LOAD_AMOUNT':
        if (minValue && maxValue) return `Monto entre $${minValue.toLocaleString()} y $${maxValue.toLocaleString()}`;
        if (minValue) return `Monto mínimo $${minValue.toLocaleString()}`;
        if (maxValue) return `Monto máximo $${maxValue.toLocaleString()}`;
        return 'Rango de monto';

      case 'LOAD_STREAK':
        if (exactValue) return `Exactamente ${exactValue} días de racha`;
        if (minValue && maxValue) return `Racha entre ${minValue} y ${maxValue} días`;
        if (minValue) return `Racha mínima de ${minValue} días`;
        if (maxValue) return `Racha máxima de ${maxValue} días`;
        return 'Racha de días consecutivos';

      case 'LOAD_COUNT_MOD':
        if (modulo) return `Cada ${modulo} cargas (resto ${moduloEquals || 0})`;
        return 'Cada N cargas';

      case 'TIME_OF_DAY':
        if (minValue !== undefined && maxValue !== undefined) {
          return `Entre las ${minValue}:00 y ${maxValue}:00 hs`;
        }
        return 'Hora del día';

      case 'DAY_OF_WEEK':
        const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
        if (exactValue !== undefined) return days[exactValue] || 'Día específico';
        return 'Día de la semana';

      case 'USER_CREATED_DAYS_AGO':
        if (minValue !== undefined && maxValue !== undefined) {
          return `Usuario con ${minValue}-${maxValue} días de antigüedad`;
        }
        if (minValue === 0 && !maxValue) return 'Usuario nuevo (registro hoy)';
        if (minValue !== undefined) return `Usuario con mínimo ${minValue} días de antigüedad`;
        if (maxValue !== undefined) return `Usuario con máximo ${maxValue} días de antigüedad`;
        return 'Antigüedad del usuario';

      case 'POINTS_REQUIRED': {
        let desc = minValue ? `Requiere ${minValue} pts` : 'Puntos requeridos';
        if (maxValue) desc += ` (carga min. $${maxValue})`;
        desc += ' - se deducen';
        return desc;
      }

      case 'REFERRAL_LOAD_COUNT': {
        const recipientText = (condition as any).recipientType === 'REFERRER'
          ? 'del referidor'
          : (condition as any).recipientType === 'BOTH'
          ? '(ambos)'
          : 'del referido';
        if (exactValue) return `Carga #${exactValue} ${recipientText}`;
        if (minValue && maxValue) return `Cargas ${minValue}-${maxValue} ${recipientText}`;
        if (minValue) return `Desde carga #${minValue} ${recipientText}`;
        if (maxValue) return `Hasta carga #${maxValue} ${recipientText}`;
        return `Todas las cargas ${recipientText}`;
      }

      case 'FIRST_LOAD_TODAY':
        return 'Primera carga del día';

      case 'FIRST_LOAD_EVER':
        return 'Primera carga del usuario (nuevo)';

      case 'REMARKETING':
        if ((condition as any).flagName) {
          return `Flag: "${(condition as any).flagName}"`;
        }
        return 'Remarketing (sin flag)';

      default:
        return conditionType;
    }
  };

  return (
    <StyledCard
      title={
        <Space>
          <GiftOutlined style={{ color: '#ac4949' }} />
          <span>Reglas de Bonus</span>
        </Space>
      }
      extra={
        <Space size={12}>
          <ExportImportButtons onImportSuccess={loadRules} />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
          >
            Crear Regla
          </Button>
        </Space>
      }
    >
{loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Empty description="Cargando reglas..." />
        </div>
      ) : rules.length === 0 ? (
        <Empty
          description="No hay reglas creadas"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        >
          <Button
            type="primary"
            onClick={handleCreate}
          >
            Crear Primera Regla
          </Button>
        </Empty>
      ) : (
<Space direction="vertical" size={12} style={{ width: '100%' }}>
          {rules.map((rule) => {
            const bonusConfig = getBonusTypeConfig(rule.bonusType);

            return (
              <Card
                key={rule.id}
                style={{
                  border: `2px solid ${rule.isActive ? bonusConfig.color : '#d9d9d9'}`,
                  borderRadius: 8,
                  background: theme === 'dark'
                    ? `linear-gradient(135deg, ${bonusConfig.bgDark} 0%, ${bonusConfig.bgDark}dd 100%)`
                    : `linear-gradient(135deg, ${bonusConfig.bg} 0%, ${bonusConfig.bg}ee 100%)`,
                  boxShadow: rule.isActive
                    ? `0 4px 12px ${bonusConfig.color}30`
                    : '0 2px 8px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease',
                  opacity: rule.isActive ? 1 : 0.7
                }}
                bodyStyle={{ padding: 12 }}
              >
                {/* FILA 1: Header con nombre, tipo y acciones */}
                <Row gutter={8} align="middle" style={{ marginBottom: 8 }}>
                  <Col flex="auto">
                    <Space size={6}>
                      {rule.isHighlighted && <StarOutlined style={{ color: '#faad14', fontSize: 14 }} />}
                      <Title level={5} style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>
                        {rule.name}
                      </Title>
                      <Badge count={`P${rule.priority}`} style={{ backgroundColor: bonusConfig.color, fontSize: 10 }} />
                    </Space>
                  </Col>
                  <Col>
                    <Space size={4}>
                      {/* Mostrar todos los bonus configs o el bonus legacy */}
                      {(rule.bonusConfigs && rule.bonusConfigs.length > 0 ? rule.bonusConfigs : [{ bonusType: rule.bonusType, bonusValue: rule.bonusValue }]).map((bonus: any, idx: number) => {
                        const bConfig = getBonusTypeConfig(bonus.bonusType);
                        const perAmountText = bonus.perAmount ? `/$${bonus.perAmount}` : '';
                        return (
                          <Tag key={idx} color={bConfig.color} style={{ margin: 0, fontSize: 11, fontWeight: 600 }}>
                            {bonus.bonusType === 'PERCENTAGE' && `${bonus.bonusValue}%${perAmountText}`}
                            {bonus.bonusType === 'FIXED_AMOUNT' && `$${bonus.bonusValue}${perAmountText}`}
                            {bonus.bonusType === 'POINTS' && `${bonus.bonusValue} pts${perAmountText}`}
                          </Tag>
                        );
                      })}
                      <Switch
                        size="small"
                        checked={rule.isActive}
                        onChange={async (checked) => {
                          try {
                            await gamificationApi.updateRule(rule.id, { ...rule, isActive: checked });
                            message.success(`Regla ${checked ? 'activada' : 'desactivada'}`);
                            loadRules();
                          } catch (error) {
                            message.error('Error al actualizar');
                          }
                        }}
                      />
                      <Button type="text" size="small" icon={<EditOutlined />} onClick={() => handleEdit(rule)} />
                      <Popconfirm
                        title="¿Eliminar?"
                        onConfirm={() => handleDelete(rule.id)}
                        okText="Sí"
                        cancelText="No"
                      >
                        <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                      </Popconfirm>
                    </Space>
                  </Col>
                </Row>

                {/* FILA 2: Tabla de información completa */}
                <div style={{
                  background: theme === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                  padding: 8,
                  borderRadius: 6,
                  fontSize: 12
                }}>
                  <Row gutter={[8, 4]}>
                    {/* COLUMNA 1: BONUS Y APILAMIENTO */}
                    <Col span={6}>
                      <div style={{ borderRight: theme === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)', paddingRight: 8 }}>
                        <Text strong style={{ fontSize: 11, display: 'block', marginBottom: 6, color: bonusConfig.color }}>
                          BONUS & APILAMIENTO ({(rule.bonusConfigs && rule.bonusConfigs.length) || 1})
                        </Text>
                        {(rule.bonusConfigs && rule.bonusConfigs.length > 0 ? rule.bonusConfigs : [{ bonusType: rule.bonusType, bonusValue: rule.bonusValue, stackBehavior: rule.stackBehavior, stackGroup: rule.stackGroup }]).map((bonus: any, idx: number) => {
                          const bConfig = getBonusTypeConfig(bonus.bonusType);
                          return (
                            <div key={idx} style={{ marginBottom: idx < (rule.bonusConfigs?.length || 1) - 1 ? 6 : 0, paddingBottom: idx < (rule.bonusConfigs?.length || 1) - 1 ? 6 : 0, borderBottom: idx < (rule.bonusConfigs?.length || 1) - 1 ? (theme === 'dark' ? '1px dashed rgba(255,255,255,0.1)' : '1px dashed rgba(0,0,0,0.1)') : 'none' }}>
                              <div style={{ marginBottom: 3 }}>
                                <Text type="secondary" style={{ fontSize: 10 }}>Tipo: </Text>
                                <Text style={{ fontSize: 11 }}>{bConfig.text}</Text>
                                <Tag color={bConfig.color} style={{ fontSize: 10, marginLeft: 4, padding: '0 4px' }}>
                                  {bonus.bonusType === 'PERCENTAGE' && `${bonus.bonusValue}%`}
                                  {bonus.bonusType === 'FIXED_AMOUNT' && `$${bonus.bonusValue}`}
                                  {bonus.bonusType === 'POINTS' && `${bonus.bonusValue} pts`}
                                </Tag>
                              </div>
                              {bonus.perAmount && (
                                <div style={{ marginBottom: 3 }}>
                                  <Text type="secondary" style={{ fontSize: 10 }}>Por cada: </Text>
                                  <Tag color="orange" style={{ fontSize: 10, padding: '0 4px', margin: 0 }}>${bonus.perAmount}</Tag>
                                </div>
                              )}
                              <div style={{ marginBottom: 3 }}>
                                <Text type="secondary" style={{ fontSize: 10 }}>Comportamiento: </Text>
                                <Text style={{ fontSize: 11 }}>
                                  {(bonus.stackBehavior || 'STACKABLE') === 'STACKABLE' && 'Apilable'}
                                  {(bonus.stackBehavior || 'STACKABLE') === 'EXCLUSIVE' && 'Exclusiva'}
                                  {(bonus.stackBehavior || 'STACKABLE') === 'REPLACE' && 'Reemplaza'}
                                </Text>
                              </div>
                              <div style={{ marginBottom: 3 }}>
                                <Text type="secondary" style={{ fontSize: 10 }}>Grupo: </Text>
                                {bonus.stackGroup ? (
                                  <Tag color="blue" style={{ fontSize: 10, padding: '0 6px', margin: 0 }}>{bonus.stackGroup}</Tag>
                                ) : (
                                  <Text type="secondary" style={{ fontSize: 11, fontStyle: 'italic' }}>Sin grupo</Text>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </Col>

                    {/* COLUMNA 2: CONDICIONES */}
                    <Col span={8}>
                      <div style={{ borderRight: theme === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)', paddingRight: 8 }}>
                        <Text strong style={{ fontSize: 11, display: 'block', marginBottom: 6, color: bonusConfig.color }}>
                          CONDICIONES ({rule.conditions.length})
                        </Text>
                        {rule.conditions.length > 0 ? (
                          <>
                            {rule.conditions.map((condition, idx) => (
                              <div key={idx} style={{ marginBottom: 3 }}>
                                <Text style={{ fontSize: 11 }}>• {getConditionDetailText(condition)}</Text>
                              </div>
                            ))}
                            {rule.resetStreakAfter && (
                              <Tag color="orange" icon={<FireOutlined />} style={{ fontSize: 10, marginTop: 4 }}>
                                Reset día {rule.resetStreakAfter}
                              </Tag>
                            )}
                          </>
                        ) : (
                          <Tag color="green" style={{ fontSize: 10 }}>✓ Aplica siempre</Tag>
                        )}
                      </div>
                    </Col>

                    {/* COLUMNA 3: RESTRICCIONES */}
                    <Col span={6}>
                      <div style={{ borderRight: theme === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)', paddingRight: 8 }}>
                        <Text strong style={{ fontSize: 11, display: 'block', marginBottom: 6, color: bonusConfig.color }}>
                          RESTRICCIONES
                        </Text>
                        <div style={{ marginBottom: 3 }}>
                          <Text type="secondary" style={{ fontSize: 10 }}>Nivel Mín: </Text>
                          <Text style={{ fontSize: 11 }}>{rule.minLevel || <Text type="secondary" style={{ fontStyle: 'italic' }}>Sin límite</Text>}</Text>
                        </div>
                        <div style={{ marginBottom: 3 }}>
                          <Text type="secondary" style={{ fontSize: 10 }}>Nivel Máx: </Text>
                          <Text style={{ fontSize: 11 }}>{rule.maxLevel || <Text type="secondary" style={{ fontStyle: 'italic' }}>Sin límite</Text>}</Text>
                        </div>
                        <div style={{ marginBottom: 3 }}>
                          <Text type="secondary" style={{ fontSize: 10 }}>Nivel Exacto: </Text>
                          {rule.levelId ? (
                            <Text style={{ fontSize: 11 }}>{levels.find(l => l.id === rule.levelId)?.name || rule.levelId}</Text>
                          ) : (
                            <Text type="secondary" style={{ fontSize: 11, fontStyle: 'italic' }}>Ninguno</Text>
                          )}
                        </div>
                        <div style={{ marginBottom: 3 }}>
                          <Text type="secondary" style={{ fontSize: 10 }}>Usos/User: </Text>
                          <Text style={{ fontSize: 11 }}>{rule.maxUsesPerUser || <Text type="secondary" style={{ fontStyle: 'italic' }}>Ilimitado</Text>}</Text>
                        </div>
                        <div style={{ marginBottom: 3 }}>
                          <Text type="secondary" style={{ fontSize: 10 }}>Usos/Día: </Text>
                          <Text style={{ fontSize: 11 }}>{rule.maxUsesPerDay || <Text type="secondary" style={{ fontStyle: 'italic' }}>Ilimitado</Text>}</Text>
                        </div>
                        <div style={{ marginBottom: 3 }}>
                          <Text type="secondary" style={{ fontSize: 10 }}>Usos Global: </Text>
                          <Text style={{ fontSize: 11 }}>{rule.maxUsesGlobal || <Text type="secondary" style={{ fontStyle: 'italic' }}>Ilimitado</Text>}</Text>
                        </div>
                      </div>
                    </Col>

                    {/* COLUMNA 4: EXTRAS */}
                    <Col span={4}>
                      <Text strong style={{ fontSize: 11, display: 'block', marginBottom: 6, color: bonusConfig.color }}>
                        EXTRAS
                      </Text>
                      <div style={{ marginBottom: 3 }}>
                        <Text type="secondary" style={{ fontSize: 10 }}>Desc: </Text>
                        {rule.description ? (
                          <Text style={{ fontSize: 11 }}>{rule.description.substring(0, 35)}{rule.description.length > 35 ? '...' : ''}</Text>
                        ) : (
                          <Text type="secondary" style={{ fontSize: 11, fontStyle: 'italic' }}>Sin descripción</Text>
                        )}
                      </div>
                      <div style={{ marginBottom: 3 }}>
                        <Text type="secondary" style={{ fontSize: 10 }}>Mensaje: </Text>
                        {rule.successMessage ? (
                          <Tag color="cyan" style={{ fontSize: 10, margin: 0 }}>Sí</Tag>
                        ) : (
                          <Text type="secondary" style={{ fontSize: 11, fontStyle: 'italic' }}>No</Text>
                        )}
                      </div>
                      <div style={{ marginBottom: 3 }}>
                        <Text type="secondary" style={{ fontSize: 10 }}>Desde: </Text>
                        {rule.validFrom ? (
                          <Text style={{ fontSize: 11 }}>{new Date(rule.validFrom).toLocaleDateString()}</Text>
                        ) : (
                          <Text type="secondary" style={{ fontSize: 11, fontStyle: 'italic' }}>Siempre</Text>
                        )}
                      </div>
                      <div>
                        <Text type="secondary" style={{ fontSize: 10 }}>Hasta: </Text>
                        {rule.validUntil ? (
                          <Text style={{ fontSize: 11 }}>{new Date(rule.validUntil).toLocaleDateString()}</Text>
                        ) : (
                          <Text type="secondary" style={{ fontSize: 11, fontStyle: 'italic' }}>Siempre</Text>
                        )}
                      </div>
                    </Col>
                  </Row>
                </div>
              </Card>
            );
          })}
        </Space>
      )}

      {/* Modal de Creación/Edición de Reglas */}
      <Modal
        title={editingRule ? 'Editar Regla' : 'Crear Nueva Regla'}
        open={modalVisible}
        onCancel={() => {
          form.resetFields();
          setModalVisible(false);
        }}
        onOk={() => form.submit()}
        width={1600}
        okText="Guardar"
        cancelText="Cancelar"
        bodyStyle={{ padding: '12px' }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          {/* Campo oculto para resetStreakAfter - siempre presente para evitar que se pierda el valor */}
          <Form.Item name="resetStreakAfter" style={{ display: 'none' }}>
            <InputNumber />
          </Form.Item>

          <Row gutter={8}>
            {/* COLUMNA 1: INFORMACIÓN BÁSICA */}
            <Col span={6}>
              <div style={{ padding: '0 6px', borderRight: '1px solid #f0f0f0', height: '480px' }}>
                <Title level={5} style={{ marginTop: 0, marginBottom: 8, fontSize: 13 }}>Información Básica</Title>

                <Form.Item
                  label={<span style={{ fontSize: 11 }}>Nombre</span>}
                  name="name"
                  rules={[
                    { required: true, message: 'Requerido' },
                    { max: 50, message: 'Máx 50 caracteres' }
                  ]}
                  style={{ marginBottom: 6 }}
                >
                  <Input placeholder="Nombre" size="small" style={{ fontSize: 11, padding: '2px 6px' }} maxLength={50} showCount />
                </Form.Item>

                <Form.Item
                  label={<span style={{ fontSize: 11 }}>Prioridad</span>}
                  name="priority"
                  tooltip="Mayor = más prioridad (default: 0)"
                  style={{ marginBottom: 6 }}
                  initialValue={0}
                >
                  <InputNumber min={0} max={100} style={{ width: '100%', fontSize: 11 }} size="small" placeholder="0" />
                </Form.Item>

                <Form.Item
                  label={<span style={{ fontSize: 11 }}>Descripción</span>}
                  name="description"
                  style={{ marginBottom: 6 }}
                  rules={[
                    { max: 200, message: 'Máx 200 caracteres' }
                  ]}
                >
                  <TextArea rows={2} placeholder="Descripción" size="small" style={{ fontSize: 11 }} maxLength={200} showCount />
                </Form.Item>

                <Form.Item
                  label={<span style={{ fontSize: 11 }}>Mensaje Éxito</span>}
                  name="successMessage"
                  tooltip="Mensaje al aplicar"
                  style={{ marginBottom: 6 }}
                  rules={[
                    { max: 150, message: 'Máx 150 caracteres' }
                  ]}
                >
                  <TextArea rows={2} placeholder="Mensaje" size="small" style={{ fontSize: 11 }} maxLength={150} showCount />
                </Form.Item>

                <Form.Item
                  name="isHighlighted"
                  valuePropName="checked"
                  style={{ marginBottom: 6 }}
                >
                  <Space size={4}>
                    <Switch size="small" />
                    <Text style={{ fontSize: 11 }}>Destacar</Text>
                  </Space>
                </Form.Item>

                <Form.Item
                  name="isActive"
                  valuePropName="checked"
                  style={{ marginBottom: 0 }}
                  initialValue={true}
                >
                  <Space size={4}>
                    <Switch size="small" defaultChecked />
                    <Text style={{ fontSize: 11 }}>Activa</Text>
                  </Space>
                </Form.Item>
              </div>
            </Col>

            {/* COLUMNA 2: CONFIGURACIÓN DE BONUS */}
            <Col span={6}>
              <div style={{ padding: '0 6px', borderRight: '1px solid #f0f0f0', height: '480px', display: 'flex', flexDirection: 'column' }}>
                <Title level={5} style={{ marginTop: 0, marginBottom: 8, fontSize: 13 }}>Config. Bonus</Title>

                <Form.Item noStyle shouldUpdate>
                  {({ getFieldValue }) => {
                    const conditions = getFieldValue('conditions') || [];
                    const bonusConfigs = getFieldValue('bonusConfigs') || [];
                    const remarketingConditions = conditions.filter((c: any) => c?.conditionType === 'REMARKETING');

                    if (remarketingConditions.length > 0) {
                      const conditionFlags = remarketingConditions
                        .map((c: any) => c.flagName)
                        .filter(Boolean)
                        .join(', ') || 'ninguna flag en condiciones';

                      const bonusFlags = bonusConfigs
                        .map((b: any) => b.remarketingFlagName)
                        .filter(Boolean)
                        .join(', ') || 'ninguna flag en bonus';

                      return (
                        <Alert
                          message="Vinculado a Remarketing"
                          description={
                            <>
                              <div>Flags en condiciones (deben estar activas): <strong>{conditionFlags}</strong></div>
                              <div style={{ marginTop: 4 }}>Flags en bonus configs (solo se aplican si coinciden): <strong>{bonusFlags}</strong></div>
                            </>
                          }
                          type="info"
                          showIcon
                          style={{ fontSize: 9, marginBottom: 8, padding: '4px 8px' }}
                        />
                      );
                    }
                    return null;
                  }}
                </Form.Item>

                <div style={{ flex: 1, overflowY: 'auto', paddingRight: '4px' }}>
                  <Form.List name="bonusConfigs">
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map(({ key, name, ...restField }) => (
                          <Card
                            key={key}
                            size="small"
                            style={{ marginBottom: 4, fontSize: 11 }}
                            bodyStyle={{ padding: 6 }}
                            headStyle={{ minHeight: '28px', padding: '0 8px' }}
                            title={
                              <Form.Item noStyle shouldUpdate>
                                {({ getFieldValue }) => {
                                  const conditions = getFieldValue('conditions') || [];
                                  const remarketingConditions = conditions.filter((c: any) => c?.conditionType === 'REMARKETING');

                                  if (remarketingConditions.length > 0) {
                                    return (
                                      <Space size={4}>
                                        <span style={{ fontSize: 10 }}>Bonus #{name + 1}</span>
                                        <Tag color="purple" style={{ fontSize: 9, margin: 0, padding: '0 4px' }}>
                                          Remarketing
                                        </Tag>
                                      </Space>
                                    );
                                  }
                                  return <span style={{ fontSize: 10 }}>Bonus #{name + 1}</span>;
                                }}
                              </Form.Item>
                            }
                            extra={
                              fields.length > 1 && (
                                <Popconfirm
                                  title="¿Eliminar esta configuración de bonus?"
                                  onConfirm={() => remove(name)}
                                  okText="Sí"
                                  cancelText="No"
                                >
                                  <Button
                                    type="text"
                                    danger
                                    size="small"
                                    icon={<DeleteOutlined style={{ fontSize: 10 }} />}
                                    style={{ padding: '0 4px', height: '20px' }}
                                  />
                                </Popconfirm>
                              )
                            }
                          >
                            <Form.Item
                              {...restField}
                              label={<span style={{ fontSize: 10 }}>Tipo</span>}
                              name={[name, 'bonusType']}
                              rules={[{ required: true, message: 'Req.' }]}
                              style={{ marginBottom: 4 }}
                            >
                              <Select size="small" style={{ fontSize: 10 }}>
                                <Option value="PERCENTAGE">% Porcentaje</Option>
                                <Option value="FIXED_AMOUNT">$ Monto Fijo</Option>
                                <Option value="POINTS">Puntos</Option>
                              </Select>
                            </Form.Item>

                            <Form.Item
                              {...restField}
                              label={<span style={{ fontSize: 10 }}>Valor</span>}
                              name={[name, 'bonusValue']}
                              rules={[
                                { required: true, message: 'Req.' },
                                { type: 'number', min: 0.01, message: 'Debe ser > 0' }
                              ]}
                              style={{ marginBottom: 4 }}
                            >
                              <InputNumber min={0.01} max={1000000} style={{ width: '100%', fontSize: 10 }} size="small" />
                            </Form.Item>

                            <Form.Item
                              {...restField}
                              label={<span style={{ fontSize: 10 }}>Cada $ monto</span>}
                              name={[name, 'perAmount']}
                              tooltip="Si se define, el valor se multiplica por cada N pesos del monto. Ej: perAmount=100, valor=10 → cada $100 da 10 pts/bonus"
                              style={{ marginBottom: 4 }}
                            >
                              <InputNumber
                                min={1}
                                max={1000000}
                                style={{ width: '100%', fontSize: 10 }}
                                size="small"
                                placeholder="Vacío = fijo"
                              />
                            </Form.Item>

                            <Form.Item noStyle shouldUpdate={(prevValues, curValues) => {
                              const prevConditions = prevValues.conditions || [];
                              const curConditions = curValues.conditions || [];
                              const prevHasReferral = prevConditions.some((c: any) => c?.conditionType === 'REFERRAL_LOAD_COUNT');
                              const curHasReferral = curConditions.some((c: any) => c?.conditionType === 'REFERRAL_LOAD_COUNT');
                              return prevHasReferral !== curHasReferral;
                            }}>
                              {({ getFieldValue }) => {
                                const conditions = getFieldValue('conditions') || [];
                                const hasReferralCondition = conditions.some((c: any) => c?.conditionType === 'REFERRAL_LOAD_COUNT');

                                return (
                                  <Form.Item
                                    {...restField}
                                    label={<span style={{ fontSize: 10 }}>Bonus para</span>}
                                    name={[name, 'recipientType']}
                                    tooltip="Define quién recibe este bonus"
                                    style={{ marginBottom: 4, display: hasReferralCondition ? 'block' : 'none' }}
                                    initialValue="REFERRER"
                                  >
                                    <Select size="small" style={{ fontSize: 10 }}>
                                      <Option value="REFERRER">Quien refiere</Option>
                                      <Option value="REFERRED">Quien fue referido</Option>
                                      <Option value="BOTH">Ambos</Option>
                                    </Select>
                                  </Form.Item>
                                );
                              }}
                            </Form.Item>

                            <Form.Item
                              {...restField}
                              label={<span style={{ fontSize: 10 }}>Apilamiento</span>}
                              name={[name, 'stackBehavior']}
                              tooltip="Cómo combina con otras"
                              style={{ marginBottom: 4 }}
                            >
                              <Select size="small" style={{ fontSize: 10 }}>
                                <Option value="STACKABLE">Apilable</Option>
                                <Option value="EXCLUSIVE">Exclusiva</Option>
                                <Option value="REPLACE">Reemplaza</Option>
                              </Select>
                            </Form.Item>

                            <Form.Item
                              {...restField}
                              label={<span style={{ fontSize: 10 }}>Grupo</span>}
                              name={[name, 'stackGroup']}
                              tooltip="Grupo apilamiento (letras, números, guiones)"
                              style={{ marginBottom: 4 }}
                              rules={[
                                { max: 50, message: 'Máx 50 caracteres' },
                                { pattern: /^[a-z0-9-_]*$/, message: 'Solo minúsculas, números, - y _' }
                              ]}
                            >
                              <Input placeholder="ej: first-load" size="small" style={{ fontSize: 10, padding: '2px 6px' }} maxLength={50} />
                            </Form.Item>

                            {/* Input condicional: solo mostrar si hay condición REMARKETING */}
                            <Form.Item noStyle shouldUpdate={(prevValues, curValues) => {
                              const prevConditions = prevValues.conditions || [];
                              const curConditions = curValues.conditions || [];
                              const prevHasRemarketing = prevConditions.some((c: any) => c?.conditionType === 'REMARKETING');
                              const curHasRemarketing = curConditions.some((c: any) => c?.conditionType === 'REMARKETING');
                              return prevHasRemarketing !== curHasRemarketing;
                            }}>
                              {({ getFieldValue }) => {
                                const conditions = getFieldValue('conditions') || [];
                                const hasRemarketingCondition = conditions.some((c: any) => c?.conditionType === 'REMARKETING');

                                if (!hasRemarketingCondition) {
                                  return null;
                                }

                                return (
                                  <Form.Item
                                    {...restField}
                                    label={<span style={{ fontSize: 10 }}>Flag de Remarketing</span>}
                                    name={[name, 'remarketingFlagName']}
                                    tooltip="Nombre de la flag que activa este bonus (ej: black-friday-2024)"
                                    style={{ marginBottom: 0 }}
                                    rules={[
                                      { max: 50, message: 'Máx 50 caracteres' },
                                      { pattern: /^[a-z0-9-_]*$/, message: 'Solo minúsculas, números, - y _' }
                                    ]}
                                  >
                                    <Input placeholder="ej: black-friday-2024" size="small" style={{ fontSize: 10, padding: '2px 6px' }} maxLength={50} />
                                  </Form.Item>
                                );
                              }}
                            </Form.Item>
                          </Card>
                        ))}
                        <Button
                          type="dashed"
                          onClick={() => add({ bonusType: 'PERCENTAGE', stackBehavior: 'STACKABLE', recipientType: 'REFERRER' })}
                          block
                          size="small"
                          icon={<PlusOutlined style={{ fontSize: 10 }} />}
                          style={{ fontSize: 10, padding: '2px 8px', height: '24px' }}
                        >
                          Agregar
                        </Button>
                      </>
                    )}
                  </Form.List>
                </div>
              </div>
            </Col>

            {/* COLUMNA 3: CONDICIONES */}
            <Col span={6}>
              <div style={{ padding: '0 6px', borderRight: '1px solid #f0f0f0', height: '480px', display: 'flex', flexDirection: 'column' }}>
                <Title level={5} style={{ marginTop: 0, marginBottom: 8, fontSize: 13 }}>Condiciones</Title>

                <div style={{ flex: 1, overflowY: 'auto', paddingRight: '4px' }}>
                  <Form.List name="conditions">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.length === 0 && (
                        <Alert
                          message="Aplica siempre"
                          description="Sin condiciones, esta regla se aplicará en todas las cargas"
                          type="success"
                          showIcon
                          style={{ marginBottom: 8, fontSize: 10 }}
                        />
                      )}
                      {fields.map(({ key, name, ...restField }) => (
                        <Card
                          key={key}
                          size="small"
                          style={{ marginBottom: 4, fontSize: 11 }}
                          bodyStyle={{ padding: 6 }}
                          headStyle={{ minHeight: '28px', padding: '0 8px' }}
                          extra={
                            fields.length >= 1 && (
                              <Popconfirm
                                title={fields.length === 1 ? "¿Eliminar? Sin condiciones la regla aplica siempre" : "¿Eliminar esta condición?"}
                                onConfirm={() => remove(name)}
                                okText="Sí"
                                cancelText="No"
                              >
                                <Button
                                  type="text"
                                  danger
                                  size="small"
                                  icon={<DeleteOutlined style={{ fontSize: 10 }} />}
                                  style={{ padding: '0 4px', height: '20px' }}
                                />
                              </Popconfirm>
                            )
                          }
                        >
                          <Form.Item
                            {...restField}
                            label={<span style={{ fontSize: 10 }}>Tipo</span>}
                            name={[name, 'conditionType']}
                            rules={[{ required: true, message: 'Req.' }]}
                            style={{ marginBottom: 4 }}
                          >
                            <Select
                              size="small"
                              style={{ fontSize: 10 }}
                              onChange={(value) => {
                                const conditions = form.getFieldValue('conditions') || [];

                                const updatedCondition: any = {
                                  conditionType: value,
                                  exactValue: null,
                                  minValue: null,
                                  maxValue: null,
                                  modulo: null,
                                  moduloEquals: null,
                                  flagName: null,
                                };

                                if (value === 'FIRST_LOAD_EVER' || value === 'FIRST_LOAD_TODAY') {
                                  updatedCondition.exactValue = 1;
                                } else if (value === 'LOAD_COUNT_MOD') {
                                  updatedCondition.modulo = 5;
                                  updatedCondition.moduloEquals = 0;
                                } else if (value === 'REMARKETING') {
                                  // REMARKETING: Informar al usuario sobre el flujo
                                  message.info('Define el nombre de la flag aquí. Luego, en los bonus configs, usa el mismo nombre para vincularlos.');
                                }

                                conditions[name] = updatedCondition;
                                form.setFieldsValue({ conditions });
                              }}
                            >
                              <Option value="LOAD_COUNT">Cantidad Cargas</Option>
                              <Option value="FIRST_LOAD_TODAY">1ra Carga Hoy</Option>
                              <Option value="FIRST_LOAD_EVER">1ra Carga Usuario</Option>
                              <Option value="LOAD_AMOUNT">Rango Monto</Option>
                              <Option value="LOAD_STREAK">Racha Días</Option>
                              <Option value="LOAD_COUNT_MOD">Cada N Cargas</Option>
                              <Option value="LOAD_COUNT_TODAY">Cargas Hoy</Option>
                              <Option value="TIME_OF_DAY">Hora del Día</Option>
                              <Option value="DAY_OF_WEEK">Día Semana</Option>
                              <Option value="USER_CREATED_DAYS_AGO">Antigüedad</Option>
                              <Option value="POINTS_REQUIRED">Puntos Req.</Option>
                              <Option value="REFERRAL_LOAD_COUNT">Bonus Referido</Option>
                              <Option value="REMARKETING">Remarketing</Option>
                            </Select>
                          </Form.Item>

                          <Form.Item noStyle shouldUpdate={(prevValues, curValues) =>
                            prevValues.conditions?.[name]?.conditionType !== curValues.conditions?.[name]?.conditionType
                          }>
                            {({ getFieldValue }) => {
                              const conditionType = getFieldValue(['conditions', name, 'conditionType']);

                              if (conditionType === 'LOAD_COUNT' || conditionType === 'LOAD_COUNT_TODAY') {
                                return (
                                  <Space direction="vertical" size={2} style={{ width: '100%' }}>
                                    <Form.Item
                                      {...restField}
                                      label={<span style={{ fontSize: 10 }}>Mín</span>}
                                      name={[name, 'minValue']}
                                      style={{ marginBottom: 2 }}
                                      rules={[
                                        ({ getFieldValue }) => ({
                                          validator(_, value) {
                                            const maxValue = getFieldValue(['conditions', name, 'maxValue']);
                                            if (!value || !maxValue || value <= maxValue) {
                                              return Promise.resolve();
                                            }
                                            return Promise.reject(new Error('Mín no puede ser mayor que Máx'));
                                          },
                                        }),
                                      ]}
                                    >
                                      <InputNumber min={0} max={10000} style={{ width: '100%', fontSize: 10 }} size="small" />
                                    </Form.Item>
                                    <Form.Item
                                      {...restField}
                                      label={<span style={{ fontSize: 10 }}>Máx</span>}
                                      name={[name, 'maxValue']}
                                      style={{ marginBottom: 0 }}
                                      rules={[
                                        ({ getFieldValue }) => ({
                                          validator(_, value) {
                                            const minValue = getFieldValue(['conditions', name, 'minValue']);
                                            if (!value || !minValue || value >= minValue) {
                                              return Promise.resolve();
                                            }
                                            return Promise.reject(new Error('Máx no puede ser menor que Mín'));
                                          },
                                        }),
                                      ]}
                                    >
                                      <InputNumber min={0} max={10000} style={{ width: '100%', fontSize: 10 }} size="small" />
                                    </Form.Item>
                                  </Space>
                                );
                              }

                              if (conditionType === 'LOAD_AMOUNT') {
                                return (
                                  <Space direction="vertical" size={2} style={{ width: '100%' }}>
                                    <Form.Item
                                      {...restField}
                                      label={<span style={{ fontSize: 10 }}>Mín $</span>}
                                      name={[name, 'minValue']}
                                      style={{ marginBottom: 2 }}
                                      rules={[
                                        ({ getFieldValue }) => ({
                                          validator(_, value) {
                                            const maxValue = getFieldValue(['conditions', name, 'maxValue']);
                                            if (!value || !maxValue || value <= maxValue) {
                                              return Promise.resolve();
                                            }
                                            return Promise.reject(new Error('Mín no puede ser mayor que Máx'));
                                          },
                                        }),
                                      ]}
                                    >
                                      <InputNumber min={0} max={1000000} style={{ width: '100%', fontSize: 10 }} size="small" />
                                    </Form.Item>
                                    <Form.Item
                                      {...restField}
                                      label={<span style={{ fontSize: 10 }}>Máx $</span>}
                                      name={[name, 'maxValue']}
                                      style={{ marginBottom: 0 }}
                                      rules={[
                                        ({ getFieldValue }) => ({
                                          validator(_, value) {
                                            const minValue = getFieldValue(['conditions', name, 'minValue']);
                                            if (!value || !minValue || value >= minValue) {
                                              return Promise.resolve();
                                            }
                                            return Promise.reject(new Error('Máx no puede ser menor que Mín'));
                                          },
                                        }),
                                      ]}
                                    >
                                      <InputNumber min={0} max={1000000} style={{ width: '100%', fontSize: 10 }} size="small" />
                                    </Form.Item>
                                  </Space>
                                );
                              }

                              if (conditionType === 'USER_CREATED_DAYS_AGO') {
                                return (
                                  <Space direction="vertical" size={2} style={{ width: '100%' }}>
                                    <Form.Item
                                      {...restField}
                                      label={<span style={{ fontSize: 10 }}>Días Mín</span>}
                                      name={[name, 'minValue']}
                                      style={{ marginBottom: 2 }}
                                      rules={[
                                        ({ getFieldValue }) => ({
                                          validator(_, value) {
                                            const maxValue = getFieldValue(['conditions', name, 'maxValue']);
                                            if (!value || !maxValue || value <= maxValue) {
                                              return Promise.resolve();
                                            }
                                            return Promise.reject(new Error('Mín no puede ser mayor que Máx'));
                                          },
                                        }),
                                      ]}
                                    >
                                      <InputNumber min={0} max={365} style={{ width: '100%', fontSize: 10 }} size="small" />
                                    </Form.Item>
                                    <Form.Item
                                      {...restField}
                                      label={<span style={{ fontSize: 10 }}>Días Máx</span>}
                                      name={[name, 'maxValue']}
                                      style={{ marginBottom: 0 }}
                                      rules={[
                                        ({ getFieldValue }) => ({
                                          validator(_, value) {
                                            const minValue = getFieldValue(['conditions', name, 'minValue']);
                                            if (!value || !minValue || value >= minValue) {
                                              return Promise.resolve();
                                            }
                                            return Promise.reject(new Error('Máx no puede ser menor que Mín'));
                                          },
                                        }),
                                      ]}
                                    >
                                      <InputNumber min={0} max={365} style={{ width: '100%', fontSize: 10 }} size="small" />
                                    </Form.Item>
                                  </Space>
                                );
                              }

                              if (conditionType === 'POINTS_REQUIRED') {
                                return (
                                  <Space direction="vertical" size={2} style={{ width: '100%' }}>
                                    <Form.Item
                                      {...restField}
                                      label={<span style={{ fontSize: 10 }}>Puntos Req.</span>}
                                      name={[name, 'minValue']}
                                      rules={[{ required: true, message: 'Req.' }]}
                                      style={{ marginBottom: 2 }}
                                    >
                                      <InputNumber min={1} max={100000} style={{ width: '100%', fontSize: 10 }} size="small" placeholder="Puntos" />
                                    </Form.Item>
                                    <Form.Item
                                      {...restField}
                                      label={<span style={{ fontSize: 10 }}>Monto Min. Carga</span>}
                                      name={[name, 'maxValue']}
                                      style={{ marginBottom: 0 }}
                                      tooltip="Monto minimo de carga para activar el canje"
                                    >
                                      <InputNumber min={0} max={1000000} style={{ width: '100%', fontSize: 10 }} size="small" placeholder="Sin minimo" />
                                    </Form.Item>
                                  </Space>
                                );
                              }

                              if (conditionType === 'REFERRAL_LOAD_COUNT') {
                                return (
                                  <Space direction="vertical" size={2} style={{ width: '100%' }}>
                                    <Alert
                                      message="Define a quién se evalúa la condición y quién recibe el bonus"
                                      type="info"
                                      showIcon
                                      style={{ fontSize: 9, marginBottom: 4, padding: '4px 8px' }}
                                    />
                                    <Form.Item
                                      {...restField}
                                      label={<span style={{ fontSize: 10 }}>Evaluar a</span>}
                                      name={[name, 'recipientType']}
                                      style={{ marginBottom: 4 }}
                                      tooltip="Define a quién se le evalúa esta condición"
                                      initialValue="REFERRED"
                                    >
                                      <Select style={{ width: '100%', fontSize: 10 }} size="small">
                                        <Option value="REFERRER">Quien refiere (referidor)</Option>
                                        <Option value="REFERRED">Quien fue referido</Option>
                                        <Option value="BOTH">Ambos</Option>
                                      </Select>
                                    </Form.Item>
                                    <Form.Item
                                      {...restField}
                                      label={<span style={{ fontSize: 10 }}>Carga #</span>}
                                      name={[name, 'exactValue']}
                                      style={{ marginBottom: 2 }}
                                      tooltip="Número de carga específica (ej: 1, 2, 3...)"
                                      rules={[
                                        ({ getFieldValue }) => ({
                                          validator(_, value) {
                                            const minValue = getFieldValue(['conditions', name, 'minValue']);
                                            const maxValue = getFieldValue(['conditions', name, 'maxValue']);
                                            if (value && (minValue || maxValue)) {
                                              return Promise.reject(new Error('No puedes usar Carga # y Rango al mismo tiempo'));
                                            }
                                            return Promise.resolve();
                                          },
                                        }),
                                      ]}
                                    >
                                      <InputNumber min={1} max={10000} placeholder="Específica" style={{ width: '100%', fontSize: 10 }} size="small" />
                                    </Form.Item>
                                    <Form.Item
                                      {...restField}
                                      label={<span style={{ fontSize: 10 }}>O Rango</span>}
                                      tooltip="Rango de cargas (ej: de la 1 a la 5)"
                                      style={{ marginBottom: 0 }}
                                    >
                                      <Space size={4}>
                                        <Form.Item
                                          {...restField}
                                          name={[name, 'minValue']}
                                          noStyle
                                          rules={[
                                            ({ getFieldValue }) => ({
                                              validator(_, value) {
                                                const exactValue = getFieldValue(['conditions', name, 'exactValue']);
                                                const maxValue = getFieldValue(['conditions', name, 'maxValue']);
                                                if (value && exactValue) {
                                                  return Promise.reject(new Error(''));
                                                }
                                                if (value && maxValue && value > maxValue) {
                                                  return Promise.reject(new Error(''));
                                                }
                                                return Promise.resolve();
                                              },
                                            }),
                                          ]}
                                        >
                                          <InputNumber min={1} max={10000} placeholder="Desde" style={{ width: 60, fontSize: 10 }} size="small" />
                                        </Form.Item>
                                        <span style={{ fontSize: 10 }}>-</span>
                                        <Form.Item
                                          {...restField}
                                          name={[name, 'maxValue']}
                                          noStyle
                                          rules={[
                                            ({ getFieldValue }) => ({
                                              validator(_, value) {
                                                const exactValue = getFieldValue(['conditions', name, 'exactValue']);
                                                const minValue = getFieldValue(['conditions', name, 'minValue']);
                                                if (value && exactValue) {
                                                  return Promise.reject(new Error(''));
                                                }
                                                if (value && minValue && value < minValue) {
                                                  return Promise.reject(new Error(''));
                                                }
                                                return Promise.resolve();
                                              },
                                            }),
                                          ]}
                                        >
                                          <InputNumber min={1} max={10000} placeholder="Hasta" style={{ width: 60, fontSize: 10 }} size="small" />
                                        </Form.Item>
                                      </Space>
                                    </Form.Item>
                                    <Text type="secondary" style={{ fontSize: 9 }}>
                                      Vacío = todas las cargas
                                    </Text>
                                  </Space>
                                );
                              }

                              if (conditionType === 'LOAD_STREAK') {
                                return (
                                  <Space direction="vertical" size={2} style={{ width: '100%' }}>
                                    <Form.Item
                                      {...restField}
                                      label={<span style={{ fontSize: 10 }}>Modo</span>}
                                      name={[name, 'streakType']}
                                      style={{ marginBottom: 2 }}
                                    >
                                      <Select size="small" style={{ fontSize: 10 }} onChange={(value) => {
                                        const conditions = form.getFieldValue('conditions') || [];
                                        const currentCondition = conditions[name];
                                        if (value === 'exact') {
                                          conditions[name] = { ...currentCondition, exactValue: 1, minValue: null, maxValue: null };
                                        } else {
                                          conditions[name] = { ...currentCondition, exactValue: null, minValue: 1, maxValue: null };
                                        }
                                        form.setFieldsValue({ conditions });
                                      }}>
                                        <Option value="exact">Exacto</Option>
                                        <Option value="range">Rango</Option>
                                      </Select>
                                    </Form.Item>

                                    <Form.Item noStyle shouldUpdate>
                                      {({ getFieldValue }) => {
                                        const streakType = getFieldValue(['conditions', name, 'streakType']);
                                        if (streakType === 'exact' || getFieldValue(['conditions', name, 'exactValue'])) {
                                          return (
                                            <>
                                              <Form.Item
                                                {...restField}
                                                label={<span style={{ fontSize: 10 }}>Día</span>}
                                                name={[name, 'exactValue']}
                                                rules={[
                                                  { required: true, message: 'Req.' },
                                                  { type: 'number', min: 1, message: 'Debe ser ≥ 1' }
                                                ]}
                                                style={{ marginBottom: 2 }}
                                              >
                                                <InputNumber min={1} max={365} style={{ width: '100%', fontSize: 10 }} size="small" />
                                              </Form.Item>
                                              <Form.Item noStyle shouldUpdate>
                                                {() => {
                                                  const exactDay = getFieldValue(['conditions', name, 'exactValue']);
                                                  const resetValue = getFieldValue('resetStreakAfter');
                                                  if (!exactDay) return null;
                                                  return (
                                                    <Space size={2}>
                                                      <Switch
                                                        size="small"
                                                        checked={resetValue === exactDay}
                                                        onChange={(checked) => form.setFieldValue('resetStreakAfter', checked ? exactDay : null)}
                                                      />
                                                      <Text style={{ fontSize: 9 }}>Reset día {exactDay}</Text>
                                                    </Space>
                                                  );
                                                }}
                                              </Form.Item>
                                            </>
                                          );
                                        }
                                        return (
                                          <>
                                            <Form.Item
                                              {...restField}
                                              label={<span style={{ fontSize: 10 }}>Mín</span>}
                                              name={[name, 'minValue']}
                                              style={{ marginBottom: 2 }}
                                              rules={[
                                                ({ getFieldValue }) => ({
                                                  validator(_, value) {
                                                    const maxValue = getFieldValue(['conditions', name, 'maxValue']);
                                                    if (!value || !maxValue || value <= maxValue) {
                                                      return Promise.resolve();
                                                    }
                                                    return Promise.reject(new Error('Mín no puede ser mayor que Máx'));
                                                  },
                                                }),
                                              ]}
                                            >
                                              <InputNumber min={1} max={365} style={{ width: '100%', fontSize: 10 }} size="small" />
                                            </Form.Item>
                                            <Form.Item
                                              {...restField}
                                              label={<span style={{ fontSize: 10 }}>Máx</span>}
                                              name={[name, 'maxValue']}
                                              style={{ marginBottom: 0 }}
                                              rules={[
                                                ({ getFieldValue }) => ({
                                                  validator(_, value) {
                                                    const minValue = getFieldValue(['conditions', name, 'minValue']);
                                                    if (!value || !minValue || value >= minValue) {
                                                      return Promise.resolve();
                                                    }
                                                    return Promise.reject(new Error('Máx no puede ser menor que Mín'));
                                                  },
                                                }),
                                              ]}
                                            >
                                              <InputNumber min={1} max={365} style={{ width: '100%', fontSize: 10 }} size="small" />
                                            </Form.Item>
                                          </>
                                        );
                                      }}
                                    </Form.Item>
                                  </Space>
                                );
                              }

                              if (conditionType === 'LOAD_COUNT_MOD') {
                                return (
                                  <Space direction="vertical" size={2} style={{ width: '100%' }}>
                                    <Form.Item
                                      {...restField}
                                      label={<span style={{ fontSize: 10 }}>Cada N</span>}
                                      name={[name, 'modulo']}
                                      style={{ marginBottom: 2 }}
                                      rules={[{ required: true, message: 'Req.' }]}
                                    >
                                      <InputNumber min={2} max={1000} style={{ width: '100%', fontSize: 10 }} size="small" />
                                    </Form.Item>
                                    <Form.Item
                                      {...restField}
                                      label={<span style={{ fontSize: 10 }}>Resto</span>}
                                      name={[name, 'moduloEquals']}
                                      style={{ marginBottom: 0 }}
                                      rules={[
                                        { required: true, message: 'Req.' },
                                        ({ getFieldValue }) => ({
                                          validator(_, value) {
                                            const modulo = getFieldValue(['conditions', name, 'modulo']);
                                            if (value == null || modulo == null) {
                                              return Promise.resolve();
                                            }
                                            if (value >= modulo) {
                                              return Promise.reject(new Error('Resto debe ser < Cada N'));
                                            }
                                            return Promise.resolve();
                                          },
                                        }),
                                      ]}
                                    >
                                      <InputNumber min={0} style={{ width: '100%', fontSize: 10 }} size="small" />
                                    </Form.Item>
                                  </Space>
                                );
                              }

                              if (conditionType === 'TIME_OF_DAY') {
                                return (
                                  <Space direction="vertical" size={2} style={{ width: '100%' }}>
                                    <Form.Item
                                      {...restField}
                                      label={<span style={{ fontSize: 10 }}>Inicio</span>}
                                      name={[name, 'minValue']}
                                      style={{ marginBottom: 2 }}
                                    >
                                      <InputNumber min={0} max={23} style={{ width: '100%', fontSize: 10 }} size="small" />
                                    </Form.Item>
                                    <Form.Item
                                      {...restField}
                                      label={<span style={{ fontSize: 10 }}>Fin</span>}
                                      name={[name, 'maxValue']}
                                      style={{ marginBottom: 0 }}
                                    >
                                      <InputNumber min={0} max={23} style={{ width: '100%', fontSize: 10 }} size="small" />
                                    </Form.Item>
                                  </Space>
                                );
                              }

                              if (conditionType === 'DAY_OF_WEEK') {
                                return (
                                  <Form.Item
                                    {...restField}
                                    label={<span style={{ fontSize: 10 }}>Día</span>}
                                    name={[name, 'exactValue']}
                                    rules={[{ required: true, message: 'Req.' }]}
                                    style={{ marginBottom: 0 }}
                                  >
                                    <Select size="small" placeholder="Día" style={{ fontSize: 10 }}>
                                      <Option value={0}>Dom</Option>
                                      <Option value={1}>Lun</Option>
                                      <Option value={2}>Mar</Option>
                                      <Option value={3}>Mié</Option>
                                      <Option value={4}>Jue</Option>
                                      <Option value={5}>Vie</Option>
                                      <Option value={6}>Sáb</Option>
                                    </Select>
                                  </Form.Item>
                                );
                              }

                              if (conditionType === 'FIRST_LOAD_TODAY' || conditionType === 'FIRST_LOAD_EVER') {
                                return (
                                  <Alert message="Sin config." type="success" showIcon style={{ padding: '2px 6px', fontSize: 10 }} />
                                );
                              }

                              if (conditionType === 'REMARKETING') {
                                return (
                                  <Space direction="vertical" size={2} style={{ width: '100%' }}>
                                    <Alert
                                      message="Condición de Remarketing"
                                      description="Define el nombre de la flag. Los bonus configs con el mismo nombre de flag se aplicarán cuando el usuario tenga esta flag activa."
                                      type="info"
                                      showIcon
                                      style={{ fontSize: 9, marginBottom: 4, padding: '4px 8px' }}
                                    />
                                    <Form.Item
                                      {...restField}
                                      label={<span style={{ fontSize: 10 }}>Nombre de Flag</span>}
                                      name={[name, 'flagName']}
                                      rules={[
                                        { required: true, message: 'Req.' },
                                        { pattern: /^[a-z0-9-_]+$/, message: 'Solo minúsculas, números, - y _' },
                                        { max: 50, message: 'Máx 50 caracteres' }
                                      ]}
                                      style={{ marginBottom: 0 }}
                                      tooltip="Nombre único de la flag. Ejemplo: black-friday-2024"
                                    >
                                      <Input
                                        placeholder="ej: black-friday-2024"
                                        size="small"
                                        style={{ fontSize: 10, padding: '2px 6px' }}
                                        maxLength={50}
                                      />
                                    </Form.Item>
                                  </Space>
                                );
                              }

                              return null;
                            }}
                          </Form.Item>
                        </Card>
                      ))}
                      <Button
                        type="dashed"
                        onClick={() => add({ conditionType: 'LOAD_COUNT', minValue: 1 })}
                        block
                        size="small"
                        icon={<PlusOutlined style={{ fontSize: 10 }} />}
                        style={{ fontSize: 10, padding: '2px 8px', height: '24px' }}
                      >
                        Agregar
                      </Button>
                    </>
                  )}
                </Form.List>
                </div>
              </div>
            </Col>

            {/* COLUMNA 4: RESTRICCIONES */}
            <Col span={6}>
              <div style={{ padding: '0 6px', height: '480px' }}>
                <Title level={5} style={{ marginTop: 0, marginBottom: 8, fontSize: 13 }}>Restricciones</Title>

                <Form.Item
                  label={<span style={{ fontSize: 11 }}>Nivel Exacto</span>}
                  name="levelId"
                  tooltip="Regla solo para este nivel específico (excluye Mín/Máx)"
                  style={{ marginBottom: 6 }}
                >
                  <Select allowClear placeholder="Ninguno" size="small" style={{ fontSize: 11 }}>
                    {levels.map(level => (
                      <Option key={level.id} value={level.id}>{level.name}</Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item noStyle shouldUpdate={(prevValues, curValues) => prevValues.levelId !== curValues.levelId}>
                  {({ getFieldValue }) => {
                    const hasLevelId = !!getFieldValue('levelId');
                    return (
                      <>
                        <Form.Item
                          label={<span style={{ fontSize: 11 }}>Nivel Mín</span>}
                          name="minLevel"
                          tooltip={hasLevelId ? 'Deshabilitado (tienes Nivel Exacto activo)' : 'Solo usuarios ≥ este nivel'}
                          style={{ marginBottom: 6 }}
                          rules={[
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                const maxLevel = getFieldValue('maxLevel');
                                if (!value || !maxLevel || value <= maxLevel) {
                                  return Promise.resolve();
                                }
                                return Promise.reject(new Error('Nivel Mín no puede ser mayor que Nivel Máx'));
                              },
                            }),
                          ]}
                        >
                          <InputNumber
                            min={1}
                            disabled={hasLevelId}
                            style={{ width: '100%', fontSize: 11 }}
                            size="small"
                          />
                        </Form.Item>

                        <Form.Item
                          label={<span style={{ fontSize: 11 }}>Nivel Máx</span>}
                          name="maxLevel"
                          tooltip={hasLevelId ? 'Deshabilitado (tienes Nivel Exacto activo)' : 'Solo usuarios ≤ este nivel'}
                          style={{ marginBottom: 6 }}
                          rules={[
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                const minLevel = getFieldValue('minLevel');
                                if (!value || !minLevel || value >= minLevel) {
                                  return Promise.resolve();
                                }
                                return Promise.reject(new Error('Nivel Máx no puede ser menor que Nivel Mín'));
                              },
                            }),
                          ]}
                        >
                          <InputNumber
                            min={1}
                            disabled={hasLevelId}
                            style={{ width: '100%', fontSize: 11 }}
                            size="small"
                          />
                        </Form.Item>
                      </>
                    );
                  }}
                </Form.Item>

                <Form.Item
                  label={<span style={{ fontSize: 11 }}>Usos x User</span>}
                  name="maxUsesPerUser"
                  tooltip="Máx por usuario (vacío = ilimitado)"
                  style={{ marginBottom: 6 }}
                  rules={[
                    { type: 'number', min: 1, message: 'Debe ser ≥ 1' }
                  ]}
                >
                  <InputNumber min={1} max={10000} style={{ width: '100%', fontSize: 11 }} size="small" placeholder="Ilimitado" />
                </Form.Item>

                <Form.Item
                  label={<span style={{ fontSize: 11 }}>Usos x Día</span>}
                  name="maxUsesPerDay"
                  tooltip="Máx diarios por usuario (vacío = ilimitado)"
                  style={{ marginBottom: 6 }}
                  rules={[
                    { type: 'number', min: 1, message: 'Debe ser ≥ 1' }
                  ]}
                >
                  <InputNumber min={1} max={10000} style={{ width: '100%', fontSize: 11 }} size="small" placeholder="Ilimitado" />
                </Form.Item>

                <Form.Item
                  label={<span style={{ fontSize: 11 }}>Usos Global</span>}
                  name="maxUsesGlobal"
                  tooltip="Total global entre todos los usuarios (vacío = ilimitado)"
                  style={{ marginBottom: 0 }}
                  rules={[
                    { type: 'number', min: 1, message: 'Debe ser ≥ 1' }
                  ]}
                >
                  <InputNumber min={1} max={1000000} style={{ width: '100%', fontSize: 11 }} size="small" placeholder="Ilimitado" />
                </Form.Item>

                <Form.Item
                  label={<span style={{ fontSize: 11 }}>Rollover Multiplier</span>}
                  name="rolloverMultiplier"
                  tooltip="Multiplicador de rollover para esta regla. Vacio = usa el default de config."
                  style={{ marginBottom: 0, marginTop: 6 }}
                  rules={[
                    { type: 'number', min: 1, message: 'Debe ser >= 1' }
                  ]}
                >
                  <InputNumber min={1} max={100} style={{ width: '100%', fontSize: 11 }} size="small" placeholder="Default" />
                </Form.Item>
              </div>
            </Col>
          </Row>
        </Form>
      </Modal>
    </StyledCard>
  );
};
